import { AxiosInstance } from '../../../utilities/requestClient';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import RSwitch from '../../ui/inputs/RSwitch';
import { cn } from '../../../utilities/cn';
import RaptorLoading from '../../feedback/RaptorLoading';
import ExportButton from '../../feedback/ExportButton';
import NoDataMessage from '../../feedback/NoDataMessage.component';

type ProfitLossEntry = {
  pct_pl: number;
  pl: number;
};

type ProfitLossEntryMatrix = ProfitLossEntry[][];

interface ComprehensiveStressTestData {
  ordered_fund_names: string[];
  ordered_stress_test_names: string[];
  client_level_stress_test_data_matrix: ProfitLossEntryMatrix;
}

const ComprehensiveStressTestPage = (props: any) => {
  const { data, isPending, error } = useComprehensiveStressTest(
    props?.positionDate,
  );

  const [showPct, setShowPct] = useState(true); // true = show pct_pl, false = show pl

  if (isPending) {
    return (
      <RaptorLoading
        centerWrap
        messages={['Loading Stress Tests', 'This may take a few seconds...']}
      />
    );
  }
  if (error)
    return <NoDataMessage message={'There was an error retrieving the data'} />;

  if (!data) {
    return <NoDataMessage message={'No Data Available'} />;
  }

  const rows = data?.ordered_fund_names || [];
  const columns = data?.ordered_stress_test_names || [];
  const matrix = data?.client_level_stress_test_data_matrix || [];

  const csvObjectData = rows.map((fundName, rowIdx) => {
    const row = matrix[rowIdx];
    const result: Record<string, string> = { Fund: fundName };

    columns.forEach((col, colIdx) => {
      const value = showPct
        ? `${row[colIdx].pct_pl}%`
        : row[colIdx].pl.toLocaleString();
      result[col] = value;
    });

    return result;
  });

  return (
    <div className="m-2 w-[calc(100vw-90px)] rounded bg-white p-2 shadow">
      <div className="w-full items-center gap-2">
        <h2 className="text-2xl font-bold text-mainblue">
          Comprehensive Stress Test
        </h2>
        <div className="flex items-center gap-4">
          <RSwitch
            enabled={showPct}
            setEnabled={setShowPct}
            label="Base Currency"
            labelOn="Percentage of Nav %"
          />
          <ExportButton
            exportData={csvObjectData}
            pdfIdentifier={`Comprehensive_Stress_Test_${props?.positionDate}`}
            fields={['Fund', ...columns]}
            fileName={`Comprehensive_Stress_Test_${props?.positionDate}`}
            selectedPositionDate={props?.positionDate}
          />
        </div>
      </div>
      <div className="scrollbar-custom relative h-[calc(100vh-15.5rem)] w-full overflow-auto">
        <table className="min-w-full table-auto border border-gray-300 text-sm">
          <thead className="bg-white">
            <tr className="sticky top-0 z-20 bg-slate-50 shadow-sm">
              <th className="sticky left-0 z-30 border bg-slate-50 p-2 text-left text-lg font-semibold text-mainblue shadow-sm">
                Fund
              </th>
              {columns.map((col, colIdx) => (
                <th
                  key={colIdx}
                  className="whitespace-nowrap border p-2 text-left text-lg font-semibold text-mainblue"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, rowIdx) => (
              <tr key={rowIdx} className="h-fit text-center even:bg-gray-50">
                <td className="sticky left-0 z-10 whitespace-nowrap text-nowrap bg-white p-2 text-left font-medium shadow-sm">
                  {rows[rowIdx]}
                </td>
                {row.map((entry, colIdx) => (
                  <td key={colIdx} className="whitespace-nowrap border p-2">
                    {showPct ? (
                      <span
                        className={cn(
                          'font-semibold',
                          entry.pct_pl > 0 && 'text-green-500',
                          entry.pct_pl < 0 && 'text-orange-600',
                        )}
                      >
                        {entry.pct_pl}%
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'font-semibold',
                          entry.pl > 0 && 'text-green-500',
                          entry.pl < 0 && 'text-orange-600',
                        )}
                      >
                        {entry.pl.toLocaleString()}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComprehensiveStressTestPage;

export function useComprehensiveStressTest(positionDate: string | null) {
  return useQuery({
    queryKey: ['comprehensive-stress-tests', positionDate],
    queryFn: () => getComprehensiveData(positionDate!),
    enabled: !!positionDate,
  });
}

export const getComprehensiveData = async (positionDate: string) => {
  return (
    await AxiosInstance.get<ComprehensiveStressTestData>(
      `manco_level_comprehensive_stress_tests/${positionDate}`,
    )
  ).data;
};
